import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { ThemeProvider } from '@/store/ThemeContext'
import { SessionProvider } from '@/store/SessionContext'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import PageChrome from '@/components/PageChrome'
import { CanvasCloseSavePage, CanvasCloseWherePage } from '@/pages/CanvasClosePage'
import PractitionerSetupPage from '@/pages/PractitionerSetupPage'
import ConsentPage from '@/pages/ConsentPage'
import YourSpacePage from '@/pages/YourSpacePage'
import LoginPage from '@/pages/LoginPage'
import BeforeWeBeginPage from '@/pages/BeforeWeBeginPage'
import ClientsPage from '@/pages/ClientsPage'
import AddClientPage from '@/pages/AddClientPage'
import ClientRecordPage from '@/pages/ClientRecordPage'
import BreathingPacerPage from '@/pages/BreathingPacerPage'
import BoxBreathingPage   from '@/pages/BoxBreathingPage'
import GroundingPage      from '@/pages/GroundingPage'
import SessionStartPage  from '@/pages/SessionStartPage'
import SessionEndPage    from '@/pages/SessionEndPage'
import SessionNotesPage  from '@/pages/SessionNotesPage'
import LandingPage       from '@/pages/LandingPage'
import ClientLoginPage   from '@/pages/ClientLoginPage'
import ClientHomePage    from '@/pages/ClientHomePage'

// Lazy-load CanvasPage so the Excalidraw bundle (large CJS) is only fetched
// when the user navigates to /session/canvas. This prevents Excalidraw's
// module initialisation from blocking or crashing the rest of the app.
const CanvasPage = lazy(() => import('@/pages/CanvasPage'))

function CanvasFallback() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100dvh',
      fontFamily: 'Lexend, system-ui, sans-serif',
      color: 'var(--page-text, #2D2D2D)',
      background: 'var(--page-bg, #FDFCFA)',
      fontSize: '1rem', fontWeight: 300,
    }}>
      Preparing canvas…
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <SessionProvider>
        <PageChrome />
        <Routes>
          {/* Shared session — both people see these */}
          {/* Session flow: /setup → /session/consent → /session/start → /session/canvas */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/client-login" element={<ClientLoginPage />} />
          <Route path="/client-home" element={<ClientHomePage />} />
          <Route path="/session/consent" element={<ConsentPage />} />
          <Route path="/session/canvas" element={
            <ErrorBoundary>
              <Suspense fallback={<CanvasFallback />}>
                <CanvasPage />
              </Suspense>
            </ErrorBoundary>
          } />
          <Route path="/session/canvas/close" element={<CanvasCloseSavePage />} />
          <Route path="/session/canvas/close/where" element={<CanvasCloseWherePage />} />

          {/* Session start — WoT check + practice suggestion + mood check */}
          <Route path="/session/start" element={<SessionStartPage />} />

          {/* Post-session — PANAS + score comparison */}
          <Route path="/session/end" element={<SessionEndPage />} />

          {/* Practitioner session notes */}
          <Route path="/session/notes" element={<SessionNotesPage />} />

          {/* Client's between-sessions recovery space */}
          <Route path="/session/after" element={<YourSpacePage />} />

          {/* Practitioner-only routes */}
          <Route path="/login"            element={<LoginPage />} />
          <Route path="/before-we-begin"  element={<BeforeWeBeginPage />} />
          <Route path="/breathe/four-six" element={<BreathingPacerPage />} />
          <Route path="/breathe/box"      element={<BoxBreathingPage />} />
          <Route path="/grounding"        element={<GroundingPage />} />
          <Route path="/clients"          element={<ClientsPage />} />
          <Route path="/clients/add"      element={<AddClientPage />} />
          <Route path="/clients/:id"      element={<ClientRecordPage />} />
          <Route path="/setup"   element={<PractitionerSetupPage />} />
        </Routes>
      </SessionProvider>
    </ThemeProvider>
  )
}

export default App
